import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ApiService } from '../../../core/services/api.service';
import { ApiEndPoints, ApiRoot } from '../../constants/api-endpoints';
import { SharedService } from '../../services/shared.service';
import { ButtonComponent } from '../button/button.component';
import { TextFieldComponent } from "../controls/text-field/text-field.component";
import { SkeletonLoaderComponent } from "../skeleton-loader/skeleton-loader.component";
import { customValidator } from '../../../Utils/validator';

@Component({
  standalone: true,
  selector: 'app-verify-email',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    TextFieldComponent,
    SkeletonLoaderComponent
  ],
  templateUrl: './verify-email.component.html',
  styleUrl: './verify-email.component.scss',
})
export class VerifyEmailComponent implements OnInit, OnDestroy {
  @Input() LoginName: string = '';
  @Input() EmailAddr: string = '';
  @Input() embedded: boolean = false;

  @Output() verified = new EventEmitter<any>();
  @Output() resendCompleted = new EventEmitter<any>();
  @Output() timerCompleted = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  otpForm: FormGroup;
  otpControl!: FormControl<string>;
  private _otpModal: boolean = true;
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;
  loading: boolean = false;
  resendCountdown: number = 120;

  // Keep template `(click)="otpModal = false"` working, while still notifying the parent.
  @Input()
  get otpModal(): boolean {
    return this._otpModal;
  }
  set otpModal(value: boolean) {
    this._otpModal = value;
    if (value === false) {
      this.closed.emit();
    }
  }

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private sharedService: SharedService
  ) {
    this.otpForm = this.fb.group({
      otp: [
        '',
        [
          Validators.required,
          Validators.maxLength(6),
          customValidator('numberOnly')
        ],
      ],
    });
    this.otpControl = this.otpForm.get('otp') as FormControl<string>;
  }

  ngOnInit(): void {
    this.startResendCountdown();
  }

  ngOnDestroy(): void {
    this.clearResendCountdown();
  }

  private normalizeLower(value: string): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private isOkResponse(response: any): boolean {
    const errorCode = response?.['Error Code'];
    const errorMsg = response?.['Error Message'];
    const verificationStatus = response?.['VerificationStatus'];
    const codeOk = errorCode === 0 || errorCode === '0' || errorCode === '' || errorCode == null;
    const msgOk = errorMsg === '' || errorMsg == null;
    return codeOk && msgOk && verificationStatus != 'Invalid';
  }

  resendOtp(): void {
    if (this.loading || this.resendCountdown > 0) return;

    const loginName = this.normalizeLower(this.LoginName);
    const emailAddr = this.normalizeLower(this.EmailAddr);

    if (!loginName || !emailAddr) {
      return;
    }

    const requestPayload = {
      body: {
        ProcessName: 'DC CP Email Verification Process WF',
        LoginName: loginName,
        EmailAddr: emailAddr,
        ReGenOTPFlg: 'Y',
      },
    };

    this.loading = true;
    this.api
      .post(ApiRoot.DCCICPIntegration, ApiEndPoints.GENERATE_EMAIL_OTP, requestPayload)
      .subscribe({
        next: (response: any) => {
          this.loading = false;
          if (this.isOkResponse(response)) {
            this.startResendCountdown();
            this.sharedService.toastMessage('SUCCESS', 'EMAIL_OTP_RESENT', 'success');
          } else {
            this.sharedService.toastMessage(
              'ERROR',
              response?.['Error Message'] || 'TECHNICAL_ERROR',
              'error'
            );
          }
          this.resendCompleted.emit(response);
        },
        error: () => {
          this.loading = false;
          this.sharedService.toastMessage('ERROR', 'TECHNICAL_ERROR', 'error');
        },
      });
  }

  get resendTimeDisplay(): string {
    const minutes = Math.floor(this.resendCountdown / 60);
    const seconds = this.resendCountdown % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  private startResendCountdown(): void {
    this.clearResendCountdown();
    this.resendCountdown = 120;
    this.countdownIntervalId = setInterval(() => {
      if (this.resendCountdown > 0) {
        this.resendCountdown--;
        if (this.resendCountdown === 0) {
          this.timerCompleted.emit();
        }
        return;
      }
      this.clearResendCountdown();
    }, 1000);
  }

  private clearResendCountdown(): void {
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  }

  verifyOtp(): void {
    if (this.loading) return;

    const otpCtl = this.otpForm.get('otp');
    otpCtl?.markAsTouched();

    if (!otpCtl || otpCtl.invalid) {
      return;
    }

    const loginName = this.normalizeLower(this.LoginName);
    const emailAddr = this.normalizeLower(this.EmailAddr);
    const otp = (otpCtl.value ?? '').toString().trim();

    if (!loginName || !emailAddr) {
      return;
    }

    const requestPayload = {
      body: {
        ProcessName: 'DC CP Validate Email Verification OTP WF',
        LoginName: loginName,
        EmailID: emailAddr,
        OTP: otp,
      },
    };

    this.loading = true;
    this.api
      .post(ApiRoot.DCCICPIntegration, ApiEndPoints.VERIFY_EMAIL_OTP, requestPayload)
      .subscribe({
        next: (response: any) => {
          this.loading = false;
          if (this.isOkResponse(response)) {
            this.sharedService.toastMessage('SUCCESS', 'EMAIL_OTP_VERIFIED', 'success');
            this.verified.emit(response);
            if (!this.embedded) {
              this.otpModal = false;
            }
            return;
          }

          this.sharedService.toastMessage(
            'ERROR',
            response?.['Error Message'] || 'INVALID_OTP',
            'error'
          );
        },
        error: () => {
          this.loading = false;
          this.sharedService.toastMessage('ERROR', 'TECHNICAL_ERROR', 'error');
        },
      });
  }
}
